import React, { useEffect, useRef, useState } from 'react';
import { App, Button, Drawer, Popover, Typography } from 'antd';
import { X, ChevronDown, Pencil, GripVertical, Plus, ChevronUp } from 'lucide-react';
import { HtsInputType } from '@evaltick/shared';
import type { HtsQuestionInput } from '@/services/api';
import { DrawerHeader } from '@/components/DrawerHeader';
import { BuilderIconButton } from '@/components/BuilderIconButton';
import { useDrawerSurface } from '@/hooks';
import { HtsQuestionForm } from '@/components/HtsQuestionForm';
import { HtsSectionsManager } from '@/components/HtsSectionsManager';
import styles from './HtsQuestionsField.module.scss';

const { Text } = Typography;

const createDraft = (order: number): HtsQuestionInput => ({
  tempId: crypto.randomUUID(),
  section: '',
  order,
  inputType: HtsInputType.TEXT,
  text: '',
});

const sectionLabel = (question: HtsQuestionInput) => question.section.trim() || 'No section';

const moveToEndOfSection = (
  list: HtsQuestionInput[],
  tempId: string,
  section: string
): HtsQuestionInput[] => {
  const trimmed = section.trim();
  const fromIndex = list.findIndex((q) => q.tempId === tempId);
  if (fromIndex === -1) return list;
  const next = [...list];
  const [moved] = next.splice(fromIndex, 1);
  let insertAt = next.length;
  for (let i = next.length - 1; i >= 0; i--) {
    if (next[i].section.trim() === trimmed) {
      insertAt = i + 1;
      break;
    }
  }
  next.splice(insertAt, 0, moved);
  return next;
};

interface SectionGroup {
  section: string;
  indices: number[];
}

const groupBySection = (questions: HtsQuestionInput[]): SectionGroup[] => {
  const groups: SectionGroup[] = [];
  questions.forEach((question, index) => {
    const section = sectionLabel(question);
    const last = groups[groups.length - 1];
    if (last && last.section === section) {
      last.indices.push(index);
    } else {
      groups.push({ section, indices: [index] });
    }
  });
  return groups;
};

interface HtsQuestionsFieldProps {
  questions: HtsQuestionInput[];
  onChange: (next: HtsQuestionInput[]) => void;
}

export const HtsQuestionsField: React.FC<HtsQuestionsFieldProps> = ({ questions, onChange }) => {
  const { modal } = App.useApp();
  const drawerSurface = useDrawerSurface(560);
  const [editingTempId, setEditingTempId] = useState<string | null>(null);
  const [isNewDraft, setIsNewDraft] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [manualSections, setManualSections] = useState<string[]>([]);
  const cardsContainerRef = useRef<HTMLDivElement>(null);
  const pendingScrollTempId = useRef<string | null>(null);

  useEffect(() => {
    const tempId = pendingScrollTempId.current;
    if (!tempId) return;
    pendingScrollTempId.current = null;
    const el = cardsContainerRef.current?.querySelector(`[data-question-tempid="${tempId}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [questions]);

  const restamp = (next: HtsQuestionInput[]) => next.map((q, order) => ({ ...q, order }));

  const sectionGroups = groupBySection(questions);

  const addQuestion = () => {
    const draft = createDraft(questions.length);
    onChange(restamp([...questions, draft]));
    setIsNewDraft(true);
    setEditingTempId(draft.tempId);
  };

  const doRemove = (tempId: string) => {
    onChange(restamp(questions.filter((q) => q.tempId !== tempId)));
    if (editingTempId === tempId) setEditingTempId(null);
  };

  const moveQuestion = (tempId: string, direction: -1 | 1) => {
    const index = questions.findIndex((q) => q.tempId === tempId);
    const target = index + direction;
    if (index === -1 || target < 0 || target >= questions.length) return;
    const next = [...questions];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(restamp(next));
  };

  const removeQuestion = (tempId: string) => {
    modal.confirm({
      title: 'Delete this question?',
      icon: null,
      maskClosable: true,
      centered: true,
      content: "This can't be undone.",
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: () => doRemove(tempId),
    });
  };

  const closeDrawer = () => {
    if (isNewDraft && editingTempId) {
      doRemove(editingTempId);
    } else {
      setEditingTempId(null);
    }
  };

  const reorderQuestions = (from: number, position: number) => {
    if (from === position) return;
    const next = [...questions];
    const [moved] = next.splice(from, 1);

    const boundaryQuestion = next[position] ?? next[next.length - 1];
    const newSection = boundaryQuestion ? sectionLabel(boundaryQuestion) : undefined;

    next.splice(position, 0, newSection !== undefined ? { ...moved, section: newSection } : moved);

    onChange(restamp(next));
  };

  const handleDragStart = (index: number) => (e: React.DragEvent) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };

  const indexForClientY = (clientY: number, excludeIndex: number): number => {
    const container = cardsContainerRef.current;
    if (!container) return 0;
    const cards = Array.from(container.querySelectorAll<HTMLElement>('[data-index]')).filter(
      (card) => Number(card.dataset.index) !== excludeIndex
    );
    let position = 0;
    for (const card of cards) {
      const rect = card.getBoundingClientRect();
      if (clientY < rect.top + rect.height / 2) return position;
      position += 1;
    }
    return position;
  };

  const boundaryIndexFor = (from: number, position: number): number | null => {
    const remaining = questions.filter((_, i) => i !== from);
    const boundaryQuestion = remaining[position] ?? remaining[remaining.length - 1];
    return boundaryQuestion ? questions.indexOf(boundaryQuestion) : null;
  };

  const handleContainerDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragIndex === null) return;
    const position = indexForClientY(e.clientY, dragIndex);
    const boundary = boundaryIndexFor(dragIndex, position);
    if (boundary !== dragOverIndex) setDragOverIndex(boundary);
  };

  const handleContainerDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (dragIndex !== null) reorderQuestions(dragIndex, indexForClientY(e.clientY, dragIndex));
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const editingQuestion = questions.find((q) => q.tempId === editingTempId) ?? null;
  const siblingQuestions = questions.filter((q) => q.tempId !== editingTempId);

  const handleFormSubmit = (values: HtsQuestionInput) => {
    const merged = questions.map((q) => (q.tempId === values.tempId ? { ...values, order: q.order } : q));
    const next = isNewDraft ? moveToEndOfSection(merged, values.tempId, values.section) : merged;
    if (isNewDraft) pendingScrollTempId.current = values.tempId;
    onChange(restamp(next));
    setEditingTempId(null);
  };

  const openEditor = (tempId: string) => {
    setIsNewDraft(false);
    setEditingTempId(tempId);
  };

  const addExtraSection = (name: string) => {
    setManualSections((prev) => (prev.includes(name) ? prev : [...prev, name]));
  };

  const removeExtraSection = (name: string) => {
    setManualSections((prev) => prev.filter((n) => n !== name));
  };

  const renameExtraSection = (oldName: string, newName: string) => {
    setManualSections((prev) => prev.map((n) => (n === oldName ? newName : n)));
  };

  const renderCard = (question: HtsQuestionInput, index: number) => {
    const isDragOverTarget = dragOverIndex === index;
    const cardClasses = [
      styles.card,
      dragIndex === index ? styles.cardDragging : '',
      isDragOverTarget && dragIndex !== index ? styles.cardDragOver : '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div
        className={cardClasses}
        key={question.tempId}
        data-index={index}
        data-question-tempid={question.tempId}
        draggable
        onDragStart={handleDragStart(index)}
        onDragEnd={handleDragEnd}
      >
        <span className={styles.dragHandle} title="Drag to reorder">
          <GripVertical style={{ width: '1em', height: '1em' }} />
        </span>
        <div className={styles.reorderArrows}>
          <button
            type="button"
            className={styles.reorderArrow}
            disabled={index === 0}
            title="Move up"
            onClick={() => moveQuestion(question.tempId, -1)}
          >
            <ChevronUp style={{ width: '1em', height: '1em' }} />
          </button>
          <button
            type="button"
            className={styles.reorderArrow}
            disabled={index === questions.length - 1}
            title="Move down"
            onClick={() => moveQuestion(question.tempId, 1)}
          >
            <ChevronDown style={{ width: '1em', height: '1em' }} />
          </button>
        </div>
        <div className={styles.cardBody}>
          <div className={styles.cardText}>{question.text.trim() || <em>Untitled question</em>}</div>
        </div>
        <div className={styles.cardActions}>
          <BuilderIconButton
            icon={<Pencil style={{ width: '1em', height: '1em' }} />}
            title="Edit"
            onClick={() => openEditor(question.tempId)}
          />
          <BuilderIconButton
            icon={<X style={{ width: '1em', height: '1em' }} />}
            title="Remove"
            onClick={() => removeQuestion(question.tempId)}
          />
        </div>
      </div>
    );
  };

  return (
    <div
      className={[styles.field, dragIndex !== null ? styles.fieldDragging : ''].filter(Boolean).join(' ')}
      ref={cardsContainerRef}
      onDragOver={dragIndex !== null ? handleContainerDragOver : undefined}
      onDrop={dragIndex !== null ? handleContainerDrop : undefined}
    >
      <div className={styles.header}>
        <div>
          <Text strong className={styles.headerLabel}>
            Questions in this questionnaire
          </Text>
          <Text className={styles.count}>
            · {questions.length} question{questions.length === 1 ? '' : 's'}
          </Text>
        </div>
        <div className={styles.headerActions}>
          <Popover
            content={
              <HtsSectionsManager
                questions={questions}
                onChange={onChange}
                extraSections={manualSections}
                onAddExtraSection={addExtraSection}
                onRemoveExtraSection={removeExtraSection}
                onRenameExtraSection={renameExtraSection}
              />
            }
            trigger="click"
            placement="bottomLeft"
            classNames={{ root: styles.sectionsPopover }}
          >
            <button type="button" className={styles.manageSectionsLink}>
              Manage sections
            </button>
          </Popover>
          <Button
            icon={<Plus style={{ width: '1em', height: '1em' }} />}
            onClick={addQuestion}
            className={styles.addButton}
          >
            Add question
          </Button>
        </div>
      </div>

      <div className={styles.cards}>
        {questions.length === 0 ? (
          <div className={styles.empty}>No questions yet — add the first one above.</div>
        ) : (
          sectionGroups.map((group) => (
            <React.Fragment key={`section-${group.indices[0]}`}>
              <div className={styles.sectionHeader}>
                <Text className={styles.sectionHeaderText}>{group.section}</Text>
              </div>
              {group.indices.map((index) => renderCard(questions[index], index))}
            </React.Fragment>
          ))
        )}
      </div>

      <Drawer
        title={
          <DrawerHeader title={isNewDraft ? 'Add Question' : 'Edit Question'} onClose={closeDrawer} />
        }
        closable={false}
        open={Boolean(editingQuestion)}
        onClose={closeDrawer}
        {...drawerSurface}
        destroyOnHidden
        className={styles.drawer}
        classNames={{ wrapper: styles.drawerWrapper }}
      >
        {editingQuestion && (
          <HtsQuestionForm
            initialValues={editingQuestion}
            siblingQuestions={siblingQuestions}
            onSubmit={handleFormSubmit}
            isEditing={!isNewDraft}
            extraSectionOptions={manualSections}
          />
        )}
      </Drawer>
    </div>
  );
};

export default HtsQuestionsField;
